export const FACT_FIND_STEPS = [
  {
    id: 1,
    step: 'Personal Details + Address History + Employment History',
    link: '/mortgage/purchase/fact-find/1',
  },
  {
    id: 2,
    step: 'Monthly Household Living Expenses',
    link: '/mortgage/purchase/fact-find/2',
  },
  {
    id: 3,
    step: 'Assets & Financial Circumstances',
    link: '/mortgage/purchase/fact-find/3',
  },
];

// Address and employment history keep going until they cover HISTORY_YEARS.
export const MAX_PREVIOUS_ENTRIES = 10;
export const HISTORY_YEARS = 3;

export const EXPENSE_FIELDS = {
  residenceCosts: {
    label: 'Primary Residence Running Costs',
    help: 'Water, electricity and gas, council rates, ongoing maintenance, repairs, household goods and furnishings. EXCLUDE rental expenses.',
  },
  groceries: {
    label: 'Groceries',
    help: 'Fruit, meat, groceries, household items, take away food and coffee (e.g. Uber Eats, Deliveroo, Menulog)',
  },
  mediaSubscriptions: {
    label: 'Telephone, Internet and Streaming Subscriptions',
    help: 'Internet, pay tv, phone and media streaming subscriptions (e.g. Netflix, Spotify, Foxtel)',
  },
  transport: {
    label: 'Transport',
    help: 'Public transport, car registration, petrol, tolls, repairs and maintenance, and rideshare costs (e.g. Uber, taxi)',
  },
  clothingPersonalCare: {
    label: 'Clothing and Personal Care',
    help: 'Clothing, shoes, accessories, hair styling and hair products, cosmetics and toiletries',
  },
  recreationEntertainment: {
    label: 'Recreation and Entertainment',
    help: 'Sport, domestic holidays, dining out, movies, electronic games and electronics',
  },
  petCare: {
    label: 'Pet Care',
    help: 'Pet food, grooming, minding services and care products',
  },
  childcare: {
    label: 'Childcare',
    help: 'Day care, childcare, preschool and sports',
  },
  childSpousalMaintenance: {
    label: 'Child and Spousal Maintenance',
    help: 'Child and Spouse support payments',
  },
  publicEducation: {
    label: 'Primary and Secondary Education',
    help: 'Public school/uni fees, books, materials, courses, higher education and professional development',
  },
  educationFees: {
    label: 'Higher Education, Vocational Training and Professional Fees',
    help: 'Further education, training and professional fees',
  },
  privateSchooling: {
    label: 'Private Schooling and Tuition',
    help: 'Private school fees',
  },
  medicalHealth: {
    label: 'Medical and Health',
    help: 'Doctor, dentist, physiotherapy, medicines. If 0, please provide reasons e.g. Employer pays for medical costs',
  },
  healthInsurance: {
    label: 'Health Insurance',
    help: 'Health insurances including sickness and personal accident insurance',
  },
  basicInsurances: {
    label: 'General Basic Insurances',
    help: 'Fire, marine, motor, accident and other miscellaneous non-life insurance',
  },
  insuranceLifeAccident: {
    label: 'Sickness and Personal Accident Insurance, Life Insurance',
    help: 'Critical illness and life insurance payments',
  },
} as const;

export type ExpenseKey = keyof typeof EXPENSE_FIELDS;
export const EXPENSE_KEYS = Object.keys(EXPENSE_FIELDS) as ExpenseKey[];

export const EXPENSE_GROUPS: { title: string; keys: ExpenseKey[] }[] = [
  {
    title: 'Home & Everyday Living',
    keys: [
      'residenceCosts',
      'groceries',
      'mediaSubscriptions',
      'transport',
      'clothingPersonalCare',
      'recreationEntertainment',
      'petCare',
    ],
  },
  {
    title: 'Family & Education',
    keys: [
      'childcare',
      'childSpousalMaintenance',
      'publicEducation',
      'educationFees',
      'privateSchooling',
    ],
  },
  {
    title: 'Health & Insurance',
    keys: [
      'medicalHealth',
      'healthInsurance',
      'basicInsurances',
      'insuranceLifeAccident',
    ],
  },
];

export const ASSET_FIELDS = {
  assetHomeContents: {
    label: 'Assets - Home Contents',
    help: '(E.g. Furniture, Electronics, Valuables)',
    required: true,
  },
  assetVehicle: {
    label: 'Assets - Vehicle',
    help: '(E.g. Motor Vehicle, Boats, Trucks)',
    required: true,
  },
  assetRetirement: {
    label: 'Retirement Contributions',
    help: '(E.g. Superannuation / MPF / CPF)',
    required: true,
  },
  assetSavings: {
    label: 'Savings',
    help: '(E.g. Cash at Bank, Term Deposits, Offset Accounts)',
    required: true,
  },
  assetRealEstate: {
    label: 'Real Estate',
    help: '(E.g. Residential Home, Investment Property, Land)',
    required: false,
  },
  assetShares: {
    label: 'Shares',
    help: '(E.g. Listed Stocks, ETFs, Managed Funds)',
    required: false,
  },
} as const;

export type AssetKey = keyof typeof ASSET_FIELDS;
export const ASSET_KEYS = Object.keys(ASSET_FIELDS) as AssetKey[];

export const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

// Indicative Minimum Living Expense table, as supplied in the fact-find
// design. Lenders update these; confirm against current lender policy.
export const MLE_HEAD = [
  'Combined Gross Income Bands (AUD)',
  'Single Adult (No Child)',
  'Couple (No Child)',
  'Couple (1 Child)',
  'Couple (2 Children)',
  'Couple (3 Children)',
  'Couple (4 Children)',
  'Couple (Additional Child)',
];

export const MLE_ROWS = [
  ['0 - 22,000', '1,040', '0', '0', '0', '0', '0', '0'],
  ['22,000 - 32,000', '1,102', '2,075', '0', '0', '0', '0', '0'],
  ['32,000 - 43,000', '1,163', '2,136', '2,466', '2,730', '0', '0', '0'],
  [
    '43,000 - 54,000',
    '1,253',
    '2,227',
    '2,556',
    '2,821',
    '3,087',
    '3,353',
    '266',
  ],
  [
    '54,000 - 64,000',
    '1,416',
    '2,389',
    '2,719',
    '2,983',
    '3,249',
    '3,515',
    '266',
  ],
  [
    '64,000 - 86,000',
    '1,624',
    '2,597',
    '2,927',
    '3,192',
    '3,457',
    '3,721',
    '265',
  ],
  [
    '86,000 - 108,000',
    '1,940',
    '2,913',
    '3,244',
    '3,508',
    '3,772',
    '4,035',
    '263',
  ],
  [
    '108,000 - 129,000',
    '2,173',
    '3,146',
    '3,476',
    '3,741',
    '4,003',
    '4,265',
    '262',
  ],
  [
    '129,000 - 151,000',
    '2,555',
    '3,528',
    '3,859',
    '4,124',
    '4,384',
    '4,645',
    '260',
  ],
  [
    '151,000 - 172,000',
    '2,700',
    '3,673',
    '4,004',
    '4,269',
    '4,529',
    '4,789',
    '260',
  ],
  [
    '172,000 - 215,000',
    '2,884',
    '3,857',
    '4,188',
    '4,453',
    '4,712',
    '4,971',
    '259',
  ],
  [
    '215,000 - 269,000',
    '3,341',
    '4,313',
    '4,645',
    '4,911',
    '5,167',
    '5,424',
    '257',
  ],
  [
    '269,000 - 323,000',
    '3,928',
    '4,900',
    '5,244',
    '5,499',
    '5,753',
    '6,007',
    '254',
  ],
  ['323,000 +', '4,020', '4,992', '5,325', '5,591', '5,844', '6,098', '254'],
];

// `api/hem/` hem_details keys (profile Household Expenditure, AUD per month)
// -> fact-find expense fields, which share the profile form's field names.
export const HEM_API_TO_EXPENSE: Record<string, ExpenseKey> = {
  primary_residence_running_costs: 'residenceCosts',
  groceries: 'groceries',
  communications_and_subscriptions: 'mediaSubscriptions',
  transport: 'transport',
  clothing_and_personal_care: 'clothingPersonalCare',
  recreation_and_entertainment: 'recreationEntertainment',
  pet_care: 'petCare',
  childcare: 'childcare',
  child_and_spousal_maintenance: 'childSpousalMaintenance',
  public_education: 'publicEducation',
  higher_education_and_fees: 'educationFees',
  private_schooling_and_tuition: 'privateSchooling',
  medical_and_health: 'medicalHealth',
  health_insurance: 'healthInsurance',
  general_basic_insurances: 'basicInsurances',
  insurance_sickness_life: 'insuranceLifeAccident',
};

// HEM indicator: a monthly total up to this far above the HEM minimum shows
// yellow ("close to the benchmark"); further above is green. Agree with
// brokers before changing.
export const HEM_CLOSE_MARGIN = 0.1;

// Step 2 action bar: expenses clients most often leave blank (short pill
// names from the design).
export const OFTEN_MISSED: { key: ExpenseKey; label: string }[] = [
  { key: 'residenceCosts', label: 'Running costs' },
  { key: 'groceries', label: 'Groceries' },
  { key: 'mediaSubscriptions', label: 'Phone & internet' },
  { key: 'transport', label: 'Transport' },
  { key: 'clothingPersonalCare', label: 'Clothing' },
  { key: 'basicInsurances', label: 'General insurance' },
];
